import React, { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { LogoutIcon, MenuAlt2Icon, XIcon } from '@heroicons/react/outline';

import LogoColor from 'img/logo_color.png';
import ExchangeLogoColor from 'img/exchange.logo_color.png';
import { useAuth } from 'contexts/Auth';
import SidebarItems from 'components/Sidebar/SidebarItems';
import SolidButton from 'components/SolidButton';
import { BUTTON_COLORS } from 'utils/enums';

export default function AdminLayout({ children }: { children?: JSX.Element }) {
  const history = useNavigate();
  const auth = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <>
      <div className="min-h-full bg-gray-100">
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog as="div" className="fixed inset-0 z-40 flex md:hidden" onClose={setSidebarOpen}>
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex w-full max-w-xs flex-1 flex-col bg-white pt-5 pb-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <div className="mb-2 flex flex-shrink-0 items-center px-4">
                  <img
                    className="h-20 w-auto"
                    src={process.env.REACT_APP_LOGO === 'exchange' ? ExchangeLogoColor : LogoColor}
                    alt="Workflow"
                  />
                </div>
                <div className="mt-5 h-0 flex-1 overflow-y-auto">
                  <nav className="space-y-1 px-2">
                    <SidebarItems
                      onCloseClick={() => setSidebarOpen(!sidebarOpen)}
                      className="mt-4 space-y-3 px-2"
                    />
                  </nav>
                </div>
              </div>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true" />
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex flex-grow flex-col overflow-y-auto border-r border-gray-200 bg-white pt-5">
            <div className="mb-2 flex flex-shrink-0 items-center px-4">
              <img
                className="w-full px-4"
                src={process.env.REACT_APP_LOGO === 'exchange' ? ExchangeLogoColor : LogoColor}
                alt="Workflow"
              />
            </div>
            <div className="mt-5 flex flex-grow flex-col">
              <nav className="flex-1 space-y-1 px-2 pb-4">
                <SidebarItems
                  onCloseClick={() => setSidebarOpen(!sidebarOpen)}
                  className="mt-4 space-y-3 px-2"
                />
              </nav>
            </div>
          </div>
        </div>
        <div className="flex min-h-full flex-1 flex-col md:pl-64">
          <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-white shadow">
            <button
              type="button"
              className="border-r border-gray-200 px-4 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <div className="flex flex-1 items-center justify-end px-4">
              <SolidButton
                className="w-auto items-center px-12"
                color={BUTTON_COLORS.PRIMARY}
                onClick={() => auth.signout(() => history('/', { replace: true }))}
              >
                Salir
                <LogoutIcon className="ml-2 h-5 w-5 text-white" />
              </SolidButton>
            </div>
          </div>

          <main className="min-h-full flex-1 bg-gray-100">{children}</main>
        </div>
      </div>
    </>
  );
}
